import { Injectable, Type, ViewContainerRef, ComponentRef } from '@angular/core';
import { count, forkJoin, Observable, shareReplay, Subject } from 'rxjs';
import { IFormComponent } from '../models/i-form-component';
import { LookupDataService } from './lookup-data.service';
import { ComponentFactoryInput } from '../models/component-factory-input';
import { ComponentFactoryData } from '../models/component-factory-data';
import { ComponentConfig } from '../models/component-config';

// Import all page components
import { ApplicantDetailsComponent } from '../pages/applicant-details/applicant-details.component';
import { GeneralEnquiryComponent } from '../pages/general-enquiry/general-enquiry.component';
import { BusinessEnquiryComponent } from '../pages/business-enquiry/business-enquiry.component';
import { EducationComponent } from '../pages/education/education.component';
import { EmployerDetailsComponent } from '../pages/employer-details/employer-details.component';
import { EmployerSponsorComponent } from '../pages/employer-sponsor/employer-sponsor.component';
import { OtherFamilyComponent } from '../pages/other-family/other-family.component';
import { RelationshipPartnerComponent } from '../pages/relationship-partner/relationship-partner.component';
import { FinishComponent } from '../pages/finish/finish.component';
import { OccupationDetailsComponent } from '../pages/occupation-details/occupation-details.component';
import { BusinessSkillsComponent } from '../pages/business-skills/business-skills.component';
import { BusinessInvestmentsComponent } from '../pages/business-investments/business-investments.component';
import { DecisionComponent } from '../pages/decision/decision.component';
import { HealthCharacterComponent } from '../pages/health-character/health-character.component';
import { PositionsComponent } from '../pages/positions/positions.component';
import { SponsorDetailsComponent } from '../pages/sponsor-details/sponsor-details.component';

@Injectable({
  providedIn: 'root'
})
export class ComponentFactoryService {
  private currentComponentRef?: ComponentRef<IFormComponent>;
  private componentLoadingSubject = new Subject<boolean>();
  private componentReadySubject = new Subject<IFormComponent | null>();


  public componentLoading$ = this.componentLoadingSubject.asObservable();
  public componentReady$ = this.componentReadySubject.asObservable();



  loadComponent(
    page: string,
    container: ViewContainerRef,
    data: ComponentFactoryData, // Now expects lookup data to be provided
    outputHandlers: { [key: string]: (value: any) => void }
  ): void {
    const config = this.getComponentConfig(page, data);

    if (!config) {
      console.error(`Component configuration not found for page: ${page}`);
      this.componentLoadingSubject.next(false);
      this.componentReadySubject.next(null);
      return;
    }

    if (!container) {
      console.warn(`Component container not available for page: ${page}`);
      this.componentLoadingSubject.next(false);
      this.componentReadySubject.next(null);
      return;
    }

    try {
      this.componentLoadingSubject.next(true);

      // Clear previous component
      this.destroyCurrentComponent();
      container.clear();

      // Create new component
      this.currentComponentRef = container.createComponent(config.component);

      // Set inputs
      this.applyInputs(this.currentComponentRef.instance, config.inputs);

      // Set outputs
      if (config.outputs) {
        this.applyOutputs(this.currentComponentRef.instance, config.outputs, outputHandlers);
      }

      // Trigger change detection
      this.currentComponentRef.changeDetectorRef.detectChanges();

      this.componentLoadingSubject.next(false);
      this.componentReadySubject.next(this.currentComponentRef.instance as IFormComponent);

    } catch (error) {
      console.error(`Error loading component for page: ${page}`, error);
      this.componentLoadingSubject.next(false);
      this.componentReadySubject.next(null);
    }
  }

  getCurrentComponent(): IFormComponent | null {
    return this.currentComponentRef ? this.currentComponentRef.instance as IFormComponent : null;
  }

  destroyCurrentComponent(): void {
    if (this.currentComponentRef) {
      this.currentComponentRef.destroy();
      this.currentComponentRef = undefined;
    }
  }


  private getComponentConfig(page: string, data: ComponentFactoryData): ComponentConfig | null {
    const baseInputs = {
      model: data.model,
      formTypes: data.formTypes
    };

    const baseOutputs = {
      formReady: 'onChildFormReady'
    };

    const configs: { [key: string]: ComponentConfig } = {
      'applicant-details': {
        component: ApplicantDetailsComponent,
        inputs: {
          ...baseInputs,
          maritalStatus: data.lookupData.maritalStatus,
          titles: data.lookupData.titles,
          visaStatus: data.lookupData.visaStatus,
          phoneTypes: data.lookupData.phoneTypes,
          countries: data.lookupData.countries,
          visaTypes: data.lookupData.visaTypes,
          isAddressForced: data.isAddressForced
        },
        outputs: baseOutputs
      },

      'general-enquiry': {
        component: GeneralEnquiryComponent,
        inputs: {
          ...baseInputs,
          educationLevels: data.lookupData.qualificationsList,
          countries: data.lookupData.countries,
          visaTypes: data.lookupData.visaTypes,
          titles: data.lookupData.titles,
          maritalStatus: data.lookupData.maritalStatus,
          phoneTypes: data.lookupData.phoneTypes,
          isAddressForced: data.isAddressForced
        },
        outputs: baseOutputs
      },

      'business-enquiry': {
        component: BusinessEnquiryComponent,
        inputs: {
          ...baseInputs,
          phoneTypes: data.lookupData.phoneTypes
        },
        outputs: baseOutputs
      },

      'education': {
        component: EducationComponent,
        inputs: {
          ...baseInputs,
          qualificationsList: data.lookupData.qualificationsList,
          countries: data.lookupData.countries
        },
        outputs: {}
      },

      'employer-details': {
        component: EmployerDetailsComponent,
        inputs: {
          ...baseInputs,
          employmentStatus: data.lookupData.employmentStatus,
          phoneTypes: data.lookupData.phoneTypes,
        },
        outputs: baseOutputs
      },

      'employer-sponsor': {
        component: EmployerSponsorComponent,
        inputs: {
          ...baseInputs,
          phoneTypes: data.lookupData.phoneTypes
        },
        outputs: baseOutputs
      },

      'other-family': {
        component: OtherFamilyComponent,
        inputs: {
          ...baseInputs,
          titles: data.lookupData.titles,
          relationTypes: data.lookupData.relationTypes,
          maritalStatuses: data.lookupData.maritalStatus,
          educationLevels: data.lookupData.qualificationsList,
          countries: data.lookupData.countries,
          visaTypes: data.lookupData.visaTypes
        },
        outputs: baseOutputs
      },

      'health-and-character': {
        component: HealthCharacterComponent,
        inputs: {
          ...baseInputs
        },
        outputs: baseOutputs
      },

      'relationship-partner': {
        component: RelationshipPartnerComponent,
        inputs: {
          ...baseInputs,
          formSubmitted: data.formSubmitted,
          relationStatuses: data.lookupData.relationshipStatuses,
          relationTypes: data.lookupData.relationTypes
        },
        outputs: baseOutputs
      },

      'finish': {
        component: FinishComponent,
        inputs: baseInputs,
        outputs: baseOutputs
      },

      'occupation': {
        component: OccupationDetailsComponent,
        inputs: {
          ...baseInputs,
            countries: data.lookupData.countries

        },
        outputs: {}
      },
      'positions': {
        component: PositionsComponent,
        inputs: {
          ...baseInputs,
          titles: data.lookupData.titles,
          maritalStatus: data.lookupData.maritalStatus,
          educationLevels: data.lookupData.qualificationsList,
          countries: data.lookupData.countries
        },
        outputs: {}
      },

      'business-skills': {
        component: BusinessSkillsComponent,
        inputs: {
          ...baseInputs,
          businessExperience: data.lookupData.businessExperience
        },
        outputs: {}
      },

      'business-investments': {
        component: BusinessInvestmentsComponent,
        inputs: {
          ...baseInputs,
          countries: data.lookupData.countries,
          industries: data.lookupData.industries,
          ownershipInterests: data.lookupData.ownershipInterests,
          assetAmounts: data.lookupData.assetAmounts,
        },
        outputs: {}
      },

      'decision': {
        component: DecisionComponent,
        inputs: {
          ...baseInputs,
          visaTypes: data.lookupData.visaTypes,
          appealDecisionBy: data.lookupData.appealDecisionBy,
          notifiedBy: data.lookupData.notifiedBy
        },
        outputs: baseOutputs
      },
      'sponsor-details': {
        component: SponsorDetailsComponent,
        inputs: {
          ...baseInputs,
          titles: data.lookupData.titles,
          employmentStatus: data.lookupData.employmentStatus,
          visaStatus: data.lookupData.visaStatus,
          maritalStatus: data.lookupData.maritalStatus,
          relationTypes: data.lookupData.relationTypes,
          parentRelationTypes: data.lookupData.parentRelationTypes,
          childRelationTypes: data.lookupData.childRelationTypes,
          phoneTypes: data.lookupData.phoneTypes,
          countries: data.lookupData.countries
        },
        outputs: baseOutputs
      },
      'sponsor-partner': {
        component: SponsorDetailsComponent,
        inputs: {
          ...baseInputs,
          titles: data.lookupData.titles,
          employmentStatus: data.lookupData.employmentStatus,
          visaStatus: data.lookupData.visaStatus,
          maritalStatus: data.lookupData.maritalStatus,
          relationTypes: data.lookupData.relationTypes,
          parentRelationTypes: data.lookupData.parentRelationTypes,
          childRelationTypes: data.lookupData.childRelationTypes,
          phoneTypes: data.lookupData.phoneTypes,
          countries: data.lookupData.countries
        },
        outputs: baseOutputs
      }
    };

    return configs[page] || null;
  }

  private applyInputs(instance: any, inputs: { [key: string]: any }): void {
    Object.keys(inputs).forEach(inputName => {
      if (inputName in instance) {
        instance[inputName] = inputs[inputName];
      }
    });
  }

  private applyOutputs(
    instance: any,
    outputs: { [key: string]: string },
    handlers: { [key: string]: (value: any) => void }
  ): void {
    Object.keys(outputs).forEach(outputName => {
      const handlerName = outputs[outputName];
      if (outputName in instance && instance[outputName]?.subscribe && handlerName in handlers) {
        instance[outputName].subscribe((value: any) => {
          handlers[handlerName](value);
        });
      }
    });
  }
}